import React, { useRef, useEffect } from "react";
import { PatientDto } from "../../types/patient";

interface PatientSearchSectionProps {
  lastName: string;
  setLastName: (val: string) => void;
  firstName: string;
  setFirstName: (val: string) => void;
  amka: string;
  setAmka: (val: string) => void;
  selectedPatient: PatientDto | null;
  setSelectedPatient: (patient: PatientDto | null) => void;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (open: boolean) => void;
  filteredPatients: PatientDto[];
  isPatientsLoading: boolean;
  onSelectPatient: (patient: PatientDto) => void;
}

export const PatientSearchSection: React.FC<PatientSearchSectionProps> = ({
  lastName,
  setLastName,
  firstName,
  setFirstName,
  amka,
  setAmka,
  selectedPatient,
  setSelectedPatient,
  isDropdownOpen,
  setIsDropdownOpen,
  filteredPatients,
  isPatientsLoading,
  onSelectPatient,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsDropdownOpen]);

  const showResults =
    isDropdownOpen &&
    !selectedPatient &&
    (lastName.length >= 2 || firstName.length >= 2 || amka.length >= 2);

  return (
    <div
      ref={dropdownRef}
      className="p-4 bg-gray-50 rounded-lg border space-y-3 relative"
    >
      <div className="flex justify-between items-center">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">
          Στοιχεία Ασθενή
        </h4>
        {selectedPatient && (
          <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">
            ✓ Επιλεγμένος Ασθενής (ID: {selectedPatient.id})
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600">
            Επώνυμο *
          </label>
          <input
            type="text"
            required
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setIsDropdownOpen(true);
              if (selectedPatient) setSelectedPatient(null);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            className="w-full mt-1 p-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="π.χ. Παπαδόπουλος"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600">
            Όνομα *
          </label>
          <input
            type="text"
            required
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setIsDropdownOpen(true);
              if (selectedPatient) setSelectedPatient(null);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            className="w-full mt-1 p-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="π.χ. Γεώργιος"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600">
            ΑΜΚΑ *
          </label>
          <input
            type="text"
            required
            maxLength={11}
            value={amka}
            onChange={(e) => {
              setAmka(e.target.value);
              setIsDropdownOpen(true);
              if (selectedPatient) setSelectedPatient(null);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            className="w-full mt-1 p-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="11 ψηφία"
          />
        </div>
      </div>

      {showResults && (
        <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto divide-y divide-gray-100 z-20 absolute left-4 right-4 top-full">
          {isPatientsLoading ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              Αναζήτηση ασθενών...
            </div>
          ) : filteredPatients.length > 0 ? (
            filteredPatients.map((p: PatientDto) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelectPatient(p)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 flex justify-between items-center transition-colors"
              >
                <div>
                  <span className="font-semibold text-gray-800">
                    {p.lastName} {p.firstName}
                  </span>
                </div>
                <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  AMKA: {p.amka}
                </span>
              </button>
            ))
          ) : (
            <div className="p-3 text-sm text-gray-500 text-center">
              Δε βρέθηκε ασθενής με αυτά τα στοιχεία.
            </div>
          )}
        </div>
      )}
    </div>
  );
};